import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import QuoteFormPopup from "@/components/QuoteFormPopup";
import heroImage from "@/assets/hero-construction.webp";
import { normaliseUKMobile, isValidEmail, getPhoneError, getEmailError } from "@/lib/phone";

const trustItems = [
"5-minute accommodation shortlist",
"In close proximity of your site",
"UK-wide coverage",
"Dedicated account manager"];


const stats = [
{ value: "300+", label: "UK Contractors Served" },
{ value: "6,000+", label: "Stays Completed" },
{ value: "8+ Hrs", label: "Saved Per Booking" }];


const Hero = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", company: "", phone: "", email: "", teamSize: "", location: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (submitted) {
      if (field === "phone") {
        const required = !value.trim() ? "Enter your phone number" : "";
        setErrors((prev) => ({ ...prev, phone: required || getPhoneError(value) || "" }));
      } else if (field === "email") {
        const required = !value.trim() ? "Enter your email address" : "";
        setErrors((prev) => ({ ...prev, email: required || getEmailError(value) || "" }));
      } else {
        setErrors((prev) => ({ ...prev, [field]: !value.trim() ? "This field is required" : "" }));
      }
    }
  };

  const handleBlur = (field: string, value: string) => {
    if (field === "phone") {
      setErrors((prev) => ({ ...prev, phone: getPhoneError(value) || "" }));
    } else if (field === "email") {
      setErrors((prev) => ({ ...prev, email: getEmailError(value) || "" }));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const newErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, val]) => { if (!val.trim()) newErrors[key] = "This field is required"; });
    const normalisedPhone = normaliseUKMobile(formData.phone);
    const phoneErr = getPhoneError(formData.phone);
    if (phoneErr) newErrors.phone = phoneErr;
    const emailErr = getEmailError(formData.email);
    if (emailErr) newErrors.email = emailErr;
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    // Fire-and-forget: lazy-load supabase only on submit to keep it out of the initial bundle
    import("@/integrations/supabase/client").then(({ supabase }) =>
      supabase.functions.invoke("submit-lead", { body: { ...formData, phone: normalisedPhone } })
    ).catch((err) => console.error("Lead submission error:", err));
    navigate("/thank-you");
  };

  const inputClass = (field: string) =>
  `px-3 py-[9px] border-[1.5px] rounded-md font-sans text-[13px] text-foreground outline-none bg-white focus:border-accent transition-colors placeholder:text-[#c4c9d4] ${errors[field] ? "border-destructive" : "border-border"}`;

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#09090b] flex items-center pt-[100px] pb-[173px] px-[130px] max-lg:px-4 max-lg:pt-8 max-lg:pb-14 max-lg:items-start">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <img src={heroImage} alt="Construction team" className="absolute w-full h-[106%] top-[-3%] left-0 object-cover" fetchPriority="high" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(130.6deg, rgba(7,7,12,0.97) 0%, rgba(12,10,7,0.88) 50%, rgba(35,23,15,0.92) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 70% 40%, rgba(252,163,17,0.07) 0%, transparent 60%)" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1180px] w-full mx-auto grid grid-cols-[700px_1fr] max-lg:grid-cols-1 gap-[60px] max-lg:gap-8 items-center">
        {/* Left column */}
        <div>
          {/* Logo - mobile only */}
          <div className="md:hidden mb-3">
            <img
              src="/lovable-uploads/eb926a39-579c-4b26-a436-01b68a7ea94b.webp"
              alt="Overnightly logo"
              width="142"
              height="24"
              className="h-6 w-auto" />
          </div>

          {/* Heading */}
          <h1 className="text-[64px] max-lg:text-[34px] font-extrabold text-white tracking-[-1.6px] max-lg:tracking-[-1px] leading-[1.1] capitalize mb-6 max-lg:mb-6 animate-[fadeUp_0.55s_ease_0.2s_both] opacity-0">
            Simplifying{" "}
            <em className="not-italic text-accent">accommodation</em> for UK contractors.
          </h1>

          {/* Trust bar */}
          <div className="bg-[rgba(252,163,17,0.08)] border border-[rgba(252,163,17,0.2)] rounded-lg p-3 max-lg:p-3 flex flex-col gap-2 max-lg:gap-2 mb-6 max-lg:mb-6 animate-[fadeUp_0.55s_ease_0.35s_both] opacity-0 w-fit md:mx-auto lg:mx-0">
            {trustItems.map((item, i) =>
            <div key={i} className="flex items-center gap-[6px] text-[14.7px] max-lg:text-[13px] font-semibold text-white/75">
                <div className="w-[18px] h-[18px] max-lg:w-[14px] max-lg:h-[14px] rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 max-lg:w-2 max-lg:h-2 text-[#09090b]" strokeWidth={3} />
                </div>
                {item}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="gap-[23px] max-md:gap-2 pt-[15px] max-md:pt-1 mb-4 animate-[fadeUp_0.55s_ease_0.5s_both] opacity-0 flex-row flex items-center justify-center">
            {stats.map((stat, i) =>
            <div key={i} className="flex items-center gap-[23px] max-md:gap-2 min-w-0">
                {i > 0 && <div className="w-px h-10 bg-[#FBA80E]/25 flex-shrink-0 md:max-lg:hidden" />}
                <div className="flex flex-col gap-[5px] max-md:gap-0.5 min-w-[130px] max-md:min-w-0 max-md:items-center max-md:text-center">
                  <div className="text-[26px] max-md:text-[20px] font-extrabold text-accent leading-[1.15] tracking-[-0.5px] text-center">{stat.value}</div>
                  <div className="text-[11px] max-md:text-[10px] font-semibold text-white/90 uppercase tracking-[0.06em] text-center">{stat.label}</div>
                </div>
              </div>
            )}
          </div>

          {/* Review Widgets */}
          <div className="animate-[fadeUp_0.55s_ease_0.6s_both] opacity-0 flex flex-row items-center gap-3 max-lg:gap-2 justify-start max-lg:justify-center">
            {/* Google Reviews Badge */}
            <a
              href="https://share.google/kqzuavu4N4pucLYNZ"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View Overnightly on Google"
              className="inline-block">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 160 90"
                width="160"
                height="90"
                role="img"
                aria-label="Excellent 5.0 on Google"
                className="h-[90px] max-lg:h-[52px] w-auto">
                <rect x="1" y="1" width="158" height="88" rx="12" fill="#1f1f23" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
                {/* Google G - centered top */}
                <g transform="translate(67.5 8)">
                  <path fill="#4285F4" d="M24.84 12.87c0-.9-.08-1.77-.23-2.6H12.67v4.92h6.83c-.29 1.59-1.19 2.93-2.53 3.83v3.18h4.09c2.39-2.2 3.78-5.45 3.78-9.33z"/>
                  <path fill="#34A853" d="M12.67 25.34c3.42 0 6.29-1.13 8.39-3.07l-4.09-3.18c-1.13.76-2.59 1.21-4.3 1.21-3.31 0-6.11-2.23-7.11-5.23H1.34v3.28C3.43 22.45 7.74 25.34 12.67 25.34z"/>
                  <path fill="#FBBC05" d="M5.56 15.07c-.25-.76-.4-1.57-.4-2.41s.14-1.65.4-2.41V6.98H1.34C.48 8.69 0 10.62 0 12.67s.48 3.97 1.34 5.68l4.22-3.28z"/>
                  <path fill="#EA4335" d="M12.67 5.03c1.87 0 3.55.64 4.87 1.91l3.63-3.63C18.96 1.27 16.08 0 12.67 0 7.74 0 3.43 2.89 1.34 6.98l4.22 3.28c1-3 3.8-5.23 7.11-5.23z"/>
                </g>
                {/* Heading */}
                <text x="80" y="52" textAnchor="middle" fontFamily="Poppins, system-ui, sans-serif" fontSize="11" fontWeight="700" fill="#ffffff">Excellent on Google</text>
                {/* Score + Stars row */}
                <g transform="translate(28 64)">
                  <text x="0" y="11" fontFamily="Poppins, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#ffffff">5.0</text>
                  <g transform="translate(22 0)">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <polygon key={i} transform={`translate(${i * 17} 0)`} fill="#FBBC05" points="7,0 8.9,4.5 13.8,4.9 10,8.1 11.2,12.9 7,10.3 2.8,12.9 4,8.1 0.2,4.9 5.1,4.5" />
                    ))}
                  </g>
                </g>
              </svg>
            </a>
            {/* Trustpilot Badge */}
            <a
              href="https://uk.trustpilot.com/review/overnightly.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View Overnightly on Trustpilot"
              className="inline-block">
              
              <img
                alt="Trustpilot 5-star rating"
                loading="lazy"
                decoding="async"
                width="242"
                height="49"
                className="h-14 max-lg:h-7 object-contain"
                src="/lovable-uploads/84915f77-9c1f-491e-a9e3-d5fc89f00440.webp" />
              
            </a>
          </div>
        </div>

        {/* Right column - Form card */}
        <div className="bg-white border border-border rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.42)] overflow-hidden animate-[fadeUp_0.6s_ease_0.3s_both] opacity-0">
          <div className="h-[3px] bg-gradient-to-r from-accent to-accent-dark" />
          <form onSubmit={handleSubmit} className="p-[29px_25px_25px] max-lg:p-5 flex flex-col gap-[3px]">
            <div className="text-[16px] font-bold text-foreground">Get My Free Shortlist</div>
            <div className="inline-flex items-center gap-[5px] self-start mt-[2px] bg-secondary border border-[rgba(252,163,17,0.2)] rounded-full px-[10px] py-[4px]">
              <span className="w-2 h-2 bg-accent rounded-full flex-shrink-0" />
              <span className="text-[11.5px] font-semibold text-accent-dark">From £25 per person/night. Cheaper than hotels</span>
            </div>
            <p className="text-[12.3px] text-muted-foreground leading-[1.5] my-[4px_0_12px] mt-1 mb-3">
              No obligation · No sourcing fees · Shortlist in 5 minutes
            </p>

            <div className="flex flex-col gap-1 mb-[9px]">
              <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Your Name <span className="text-destructive">*</span></label>
              <input required value={formData.name} onChange={(e) => updateField("name", e.target.value)} className={inputClass("name")} placeholder="Your Name" />
              {errors.name && (
                <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.name}</span>
              )}
            </div>
            <div className="flex flex-col gap-1 mb-[9px]">
              <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Company <span className="text-destructive">*</span></label>
              <input required value={formData.company} onChange={(e) => updateField("company", e.target.value)} className={inputClass("company")} placeholder="Company" />
              {errors.company && (
                <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.company}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-[9px]">
              <div className="flex flex-col gap-1 mb-[9px]">
                <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Phone <span className="text-destructive">*</span></label>
                <input required value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} onBlur={(e) => handleBlur("phone", e.target.value)} inputMode="tel" autoComplete="tel" className={inputClass("phone")} placeholder="07XXX XXXXXX" />
                {errors.phone && (
                  <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.phone}</span>
                )}
              </div>
              <div className="flex flex-col gap-1 mb-[9px]">
                <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Email <span className="text-destructive">*</span></label>
                <input required type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} onBlur={(e) => handleBlur("email", e.target.value)} className={inputClass("email")} placeholder="Email@co.com" />
                {errors.email && (
                  <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.email}</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-[9px]">
              <div className="flex flex-col gap-1 mb-[9px]">
                <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Team Size <span className="text-destructive">*</span></label>
                <select required aria-label="Team Size" value={formData.teamSize} onChange={(e) => updateField("teamSize", e.target.value)} className={`px-3 py-[9px] border-[1.5px] rounded-md font-sans text-[13px] text-foreground outline-none bg-white focus:border-accent transition-colors appearance-none ${errors.teamSize ? "border-destructive" : "border-border"}`}>
                  <option value="">Select…</option>
                  <option>2–5</option>
                  <option>6–15</option>
                  <option>16–30</option>
                  <option>31–50</option>
                  <option>50+</option>
                </select>
                {errors.teamSize && (
                  <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.teamSize}</span>
                )}
              </div>
              <div className="flex flex-col gap-1 mb-[9px]">
                <label className="text-[10.6px] font-bold text-foreground/65 uppercase tracking-[0.08em]">Project Location <span className="text-destructive">*</span></label>
                <input required value={formData.location} onChange={(e) => updateField("location", e.target.value)} className={inputClass("location")} placeholder="Location" />
                {errors.location && (
                  <span className="text-[10px] text-destructive font-medium mt-[2px]">{errors.location}</span>
                )}
              </div>
            </div>
            <button type="submit" className="w-full py-3 bg-accent hover:bg-[#fca311] text-foreground font-bold text-[13.5px] border-none rounded-md cursor-pointer mt-1 transition-all hover:-translate-y-px flex items-center justify-center gap-2">
              Get My Free Shortlist <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-center mt-[10px] text-[9.5px] font-bold text-[#6b7280] tracking-[0.07em] uppercase">
              We never share your info with anyone
            </p>
          </form>
        </div>
      </div>
    </section>);

};

export const MobileCTA = () => {
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);

  useEffect(() => {
    const footer = document.querySelector('footer');
    if (!footer) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsFooterVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isFooterVisible) setShouldRender(true);
  }, [isFooterVisible]);

  if (!shouldRender) return null;

  return (
    <div
      className={`hidden max-lg:block fixed bottom-0 left-0 right-0 z-[90] bg-[#09090b] p-[12px_20px] transition-opacity duration-300 ease-out ${
      isFooterVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'}`
      }
      onTransitionEnd={() => {if (isFooterVisible) setShouldRender(false);}}>
      
      <div className="flex items-center gap-3">
        <QuoteFormPopup>
          <button className="flex-1 py-3 bg-accent text-foreground font-bold text-sm rounded-lg">
            Get My Free Shortlist
          </button>
        </QuoteFormPopup>
        <a
          href="tel:01157181937"
          className="flex-shrink-0 w-12 h-12 rounded-lg bg-accent flex items-center justify-center"
          aria-label="Call us"
        >
          <Phone className="w-5 h-5 text-foreground" />
        </a>
      </div>
    </div>);

};


export default Hero;